import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { Package, Plus, Edit, Trash2, ShoppingCart, FileText, AlertTriangle, Search, X, Upload, Settings as SettingsIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function InventoryPage() {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [tab, setTab] = useState("products");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editProd, setEditProd] = useState(null);
  const [restockProd, setRestockProd] = useState(null);
  const [saleOpen, setSaleOpen] = useState(false);
  const [saleStartProduct, setSaleStartProduct] = useState(null);
  const [qrCfgOpen, setQrCfgOpen] = useState(false);

  const canWrite = user?.role === "admin" || user?.permissions?.includes("write");
  const isAdmin  = user?.role === "admin";

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get("/inventory"), api.get("/inventory/sales")]);
      setProducts(p.data || []);
      setSales(s.data || []);
    } catch { toast.error("Konnte Lager nicht laden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const filtered = products.filter(p =>
    !search ||
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.sku?.toLowerCase().includes(search.toLowerCase())
  );
  const lowStock = products.filter(p => (p.min_stock||0) > 0 && p.quantity <= p.min_stock);

  const handleDelete = async (p) => {
    const ok = await confirm({
      title: "Produkt löschen?",
      description: `"${p.name}" wird unwiderruflich gelöscht.`,
      confirmText: "Löschen", destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/inventory/${p.id}`); toast.success("Produkt gelöscht"); loadAll(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const downloadPdf = async (saleId, invNum) => {
    try {
      const r = await api.get(`/inventory/sales/${saleId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${invNum}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF konnte nicht geladen werden"); }
  };

  const startSaleWith = (p) => { setSaleStartProduct(p); setSaleOpen(true); };
  const startEmptySale = () => { setSaleStartProduct(null); setSaleOpen(true); };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-bold flex items-center gap-3"><Package className="w-8 h-8" /> Lager</h1>
          <p className="text-sm text-muted-foreground mt-1">Produkte, Bestand und Verkäufe verwalten</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button onClick={()=>setQrCfgOpen(true)} className="px-3 py-2 border border-border hover:bg-secondary text-sm flex items-center gap-2 rounded-sm">
              <SettingsIcon size={14} /> QR-Bill
            </button>
          )}
          {canWrite && (
            <>
              <button onClick={startEmptySale} className="px-4 py-2 border border-primary text-primary hover:bg-primary/10 transition flex items-center gap-2 text-sm font-semibold rounded-sm">
                <ShoppingCart size={14} /> Verkauf
              </button>
              <button onClick={()=>setEditProd({})} className="px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-2 text-sm font-semibold rounded-sm">
                <Plus size={14} /> Produkt
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Produkte" value={products.length} />
        <Stat label="Lagerbestand" value={products.reduce((s,p)=>s+(p.quantity||0),0)} />
        <Stat label="Niedrig" value={lowStock.length} warn={lowStock.length>0} />
        <Stat label="Verkäufe" value={sales.length} />
      </div>

      <div className="border-b border-border flex gap-0">
        <TabBtn active={tab==="products"} onClick={()=>setTab("products")}>Produkte</TabBtn>
        <TabBtn active={tab==="sales"} onClick={()=>setTab("sales")}>Verkäufe ({sales.length})</TabBtn>
      </div>

      {tab === "products" && (
        <>
          <div className="flex items-center gap-2 max-w-md">
            <Search size={14} className="text-muted-foreground" />
            <input type="text" value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Name oder Artikelnummer..."
              className="flex-1 px-3 py-2 border border-border bg-background text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          {loading ? <div className="text-sm text-muted-foreground">Wird geladen...</div>
           : filtered.length === 0 ? <div className="text-sm text-muted-foreground p-8 border border-dashed border-border text-center">{search ? `Keine Produkte für "${search}"` : "Noch keine Produkte."}</div>
           : <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30">
                  <tr className="text-left text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    <th className="px-4 py-3">Produkt</th>
                    <th className="px-4 py-3">Art.-Nr.</th>
                    <th className="px-4 py-3 text-center">URL</th>
                    <th className="px-4 py-3 text-right">Preis</th>
                    <th className="px-4 py-3 text-right">Bestand</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => {
                    const low = (p.min_stock||0) > 0 && p.quantity <= p.min_stock;
                    return (
                      <tr key={p.id} className="border-t border-border hover:bg-secondary/30">
                        <td className="px-4 py-3">
                          <div className="font-semibold">{p.name}</div>
                          {p.description && <div className="text-xs text-muted-foreground line-clamp-1">{p.description}</div>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{p.sku || "-"}</td>
                        <td className="px-4 py-3 text-center">
                          {p.url ? (
                            <a href={p.url} target="_blank" rel="noopener noreferrer"
                               onClick={e=>e.stopPropagation()}
                               className="inline-flex items-center justify-center p-1.5 hover:bg-secondary text-primary rounded-sm"
                               title={p.url}>
                              <ExternalLink size={14} />
                            </a>
                          ) : <span className="text-muted-foreground text-xs">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">CHF {Number(p.price||0).toFixed(2)}</td>
                        <td className={`px-4 py-3 text-right font-mono ${low ? "text-destructive font-bold" : ""}`}>
                          {low && <AlertTriangle size={12} className="inline mr-1" />}{p.quantity}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {canWrite && (<>
                            <button onClick={()=>startSaleWith(p)} disabled={p.quantity<=0} className="p-1.5 hover:bg-primary/10 text-primary disabled:opacity-30 disabled:cursor-not-allowed" title="Verkaufen"><ShoppingCart size={14} /></button>
                            <button onClick={()=>setRestockProd(p)} className="p-1.5 hover:bg-secondary" title="Bestand"><Upload size={14} /></button>
                            <button onClick={()=>setEditProd(p)} className="p-1.5 hover:bg-secondary" title="Bearbeiten"><Edit size={14} /></button>
                            <button onClick={()=>handleDelete(p)} className="p-1.5 hover:bg-destructive/10 text-destructive" title="Löschen"><Trash2 size={14} /></button>
                          </>)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          }
        </>
      )}

      {tab === "sales" && (
        <div className="border border-border bg-card overflow-x-auto">
          {sales.length === 0 ? <div className="text-sm text-muted-foreground p-8 text-center">Noch keine Verkäufe.</div>
           : <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr className="text-left text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  <th className="px-4 py-3">Rechnung</th>
                  <th className="px-4 py-3">Datum</th>
                  <th className="px-4 py-3">Kunde</th>
                  <th className="px-4 py-3 text-right">Positionen</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {sales.map(s => (
                  <tr key={s.id} className="border-t border-border hover:bg-secondary/30">
                    <td className="px-4 py-3 font-mono">{s.invoice_number}</td>
                    <td className="px-4 py-3">{s.date ? new Date(s.date).toLocaleDateString("de-CH") : ""}</td>
                    <td className="px-4 py-3">{s.customer?.company || s.customer?.name || "-"}</td>
                    <td className="px-4 py-3 text-right font-mono">{s.items?.length || 0}</td>
                    <td className="px-4 py-3 text-right font-mono">CHF {Number(s.total||0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={()=>downloadPdf(s.id, s.invoice_number)} className="px-3 py-1.5 text-xs border border-border hover:bg-secondary flex items-center gap-1 ml-auto"><FileText size={12} /> PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      )}

      {editProd !== null && <ProductDialog product={editProd} onClose={()=>setEditProd(null)} onSaved={()=>{ setEditProd(null); loadAll(); }} />}
      {restockProd && <RestockDialog product={restockProd} onClose={()=>setRestockProd(null)} onSaved={()=>{ setRestockProd(null); loadAll(); }} />}
      {saleOpen && <SaleDialog products={products} startProduct={saleStartProduct}
        onClose={()=>{ setSaleOpen(false); setSaleStartProduct(null); }}
        onSaved={(sale)=>{ setSaleOpen(false); setSaleStartProduct(null); loadAll(); downloadPdf(sale.id, sale.invoice_number); }} />}
      {qrCfgOpen && <QRBillConfigDialog onClose={()=>setQrCfgOpen(false)} />}
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className={`border border-border bg-card p-4 ${warn ? "border-destructive/50" : ""}`}>
      <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
      <p className={`text-2xl tracking-tight font-bold mt-1 ${warn ? "text-destructive" : ""}`}>{value}</p>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{children}</button>
  );
}

function ProductDialog({ product, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({
    name: product.name || "", description: product.description || "",
    sku: product.sku || "", url: product.url || "",
    price: product.price || 0, quantity: product.quantity || 0, min_stock: product.min_stock || 0,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name fehlt"); return; }
    setSaving(true);
    try {
      const data = { ...form, price: parseFloat(form.price)||0, quantity: parseInt(form.quantity)||0, min_stock: parseInt(form.min_stock)||0 };
      if (isNew) { await api.post("/inventory", data); toast.success("Produkt angelegt"); }
      else      { await api.put(`/inventory/${product.id}`, data); toast.success("Produkt aktualisiert"); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={isNew ? "Neues Produkt" : "Produkt bearbeiten"} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Produkt-Name *"><input type="text" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
        <Field label="Beschreibung"><textarea rows={2} value={form.description} onChange={e=>setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Artikelnummer"><input type="text" value={form.sku} onChange={e=>setForm({...form, sku: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
          <Field label="URL"><input type="url" value={form.url} onChange={e=>setForm({...form, url: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Preis (CHF)"><input type="number" step="0.01" value={form.price} onChange={e=>setForm({...form, price: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
          <Field label="Menge"><input type="number" value={form.quantity} onChange={e=>setForm({...form, quantity: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
          <Field label="Min-Bestand"><input type="number" value={form.min_stock} onChange={e=>setForm({...form, min_stock: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-secondary">Abbrechen</button>
        <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-primary text-primary-foreground font-semibold disabled:opacity-50">{saving ? "..." : "Speichern"}</button>
      </div>
    </Modal>
  );
}

function RestockDialog({ product, onClose, onSaved }) {
  const [qty, setQty] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post(`/inventory/${product.id}/restock`, { quantity: parseInt(qty), note }); toast.success("Bestand angepasst"); onSaved(); }
    catch { toast.error("Anpassung fehlgeschlagen"); } finally { setSaving(false); }
  };
  return (
    <Modal title={`Bestand: ${product.name}`} onClose={onClose}>
      <p className="text-sm text-muted-foreground mb-3">Aktueller Bestand: <span className="font-mono font-bold">{product.quantity}</span></p>
      <Field label="Änderung (positiv = einbuchen, negativ = ausbuchen)"><input type="number" value={qty} onChange={e=>setQty(e.target.value)} autoFocus className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
      <div className="mt-3"></div>
      <Field label="Notiz (optional)"><input type="text" value={note} onChange={e=>setNote(e.target.value)} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-secondary">Abbrechen</button>
        <button onClick={save} disabled={saving || !qty} className="px-4 py-2 text-sm bg-primary text-primary-foreground font-semibold disabled:opacity-50">{saving ? "..." : "Speichern"}</button>
      </div>
    </Modal>
  );
}

function SaleDialog({ products, startProduct, onClose, onSaved }) {
  const [cart, setCart] = useState(startProduct ? [{ product_id: startProduct.id, quantity: 1, _ref: startProduct }] : []);
  const [productPick, setProductPick] = useState("");
  const [cust, setCust] = useState({ customer_name: "", customer_company: "", customer_address: "", customer_zip: "", customer_city: "", customer_email: "", customer_phone: "" });
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");

  useEffect(() => { api.get("/customers").then(r => setCustomers(r.data || [])).catch(()=>{}); }, []);

  const addProduct = () => {
    if (!productPick) return;
    const p = products.find(x => x.id === productPick);
    if (!p) return;
    if (cart.some(it => it.product_id === p.id)) { toast.info("Produkt bereits im Warenkorb"); return; }
    if (p.quantity <= 0) { toast.error("Bestand 0"); return; }
    setCart([...cart, { product_id: p.id, quantity: 1, _ref: p }]);
    setProductPick("");
  };
  const updateQty = (idx, q) => {
    const next = [...cart];
    const max = next[idx]._ref?.quantity || 1;
    next[idx].quantity = Math.max(1, Math.min(max, parseInt(q)||1));
    setCart(next);
  };
  const removeItem = (idx) => setCart(cart.filter((_,i) => i !== idx));

  const subtotal = cart.reduce((s,it) => s + (it._ref?.price || 0) * it.quantity, 0);
  const tax = subtotal * 0.081;
  const total = subtotal + tax;

  const save = async () => {
    if (cart.length === 0) { toast.error("Mindestens ein Produkt"); return; }
    if (!customerId && !cust.customer_name && !cust.customer_company) { toast.error("Kunden-Name oder Firma erforderlich"); return; }
    setSaving(true);
    try {
      const payload = {
        items: cart.map(it => ({ product_id: it.product_id, quantity: it.quantity })),
        customer_id: customerId || null, ...cust, notes, tax_rate: 8.1,
      };
      const r = await api.post("/inventory/sales", payload);
      toast.success(`Rechnung ${r.data.invoice_number} erstellt`);
      onSaved(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Verkauf fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Neuer Verkauf" onClose={onClose} wide>
      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Warenkorb</p>
          {cart.length === 0 && <p className="text-sm text-muted-foreground py-2">Keine Produkte. Wähle unten ein Produkt aus.</p>}
          {cart.length > 0 && (
            <div className="border border-border rounded-sm divide-y divide-border">
              {cart.map((it, idx) => (
                <div key={idx} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <div className="flex-1">
                    <div className="font-semibold">{it._ref?.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{it._ref?.sku} - CHF {Number(it._ref?.price||0).toFixed(2)} - Bestand: {it._ref?.quantity}</div>
                  </div>
                  <input type="number" min="1" max={it._ref?.quantity||1} value={it.quantity} onChange={e=>updateQty(idx, e.target.value)}
                    className="w-16 px-2 py-1 border border-border bg-background text-sm rounded-sm font-mono text-center" />
                  <div className="w-24 text-right font-mono text-sm">CHF {((it._ref?.price||0)*it.quantity).toFixed(2)}</div>
                  <button onClick={()=>removeItem(idx)} className="p-1 hover:bg-destructive/10 text-destructive"><X size={14} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <select value={productPick} onChange={e=>setProductPick(e.target.value)} className="flex-1 px-3 py-2 border border-border bg-background text-sm rounded-sm">
              <option value="">- Produkt wählen -</option>
              {products.filter(p => p.quantity > 0 && !cart.some(it => it.product_id === p.id)).map(p => (
                <option key={p.id} value={p.id}>{p.name} (CHF {Number(p.price).toFixed(2)} - {p.quantity} Stk)</option>
              ))}
            </select>
            <button onClick={addProduct} disabled={!productPick} className="px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-sm font-semibold flex items-center gap-1 disabled:opacity-50"><Plus size={14} /> Hinzu</button>
          </div>
        </div>

        <Field label="Bestehender Kunde">
          <select value={customerId} onChange={e=>setCustomerId(e.target.value)} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm">
            <option value="">- Manuell eingeben -</option>
            {customers.map(c => (
              <option key={c.id} value={c.id}>
                {c.company ? c.company : ""}{c.company && (c.first_name || c.last_name) ? " - " : ""}{c.first_name} {c.last_name}
              </option>
            ))}
          </select>
        </Field>

        {!customerId && (
          <>
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Kunde manuell</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><input type="text" value={cust.customer_name} onChange={e=>setCust({...cust, customer_name: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
              <Field label="Firma"><input type="text" value={cust.customer_company} onChange={e=>setCust({...cust, customer_company: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
              <Field label="Adresse"><input type="text" value={cust.customer_address} onChange={e=>setCust({...cust, customer_address: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="PLZ"><input type="text" value={cust.customer_zip} onChange={e=>setCust({...cust, customer_zip: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
                <Field label="Ort"><input type="text" value={cust.customer_city} onChange={e=>setCust({...cust, customer_city: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
              </div>
              <Field label="Email"><input type="email" value={cust.customer_email} onChange={e=>setCust({...cust, customer_email: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
              <Field label="Telefon"><input type="tel" value={cust.customer_phone} onChange={e=>setCust({...cust, customer_phone: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
            </div>
          </>
        )}

        <Field label="Bemerkung"><textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>

        <div className="border border-border bg-muted/20 p-3 rounded-sm space-y-1 text-sm font-mono">
          <div className="flex justify-between"><span>Zwischensumme</span><span>CHF {subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between text-muted-foreground"><span>MwSt. 8.1%</span><span>CHF {tax.toFixed(2)}</span></div>
          <div className="flex justify-between font-bold text-base border-t border-border pt-1 mt-1"><span>Total</span><span>CHF {total.toFixed(2)}</span></div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-secondary">Abbrechen</button>
        <button onClick={save} disabled={saving || cart.length===0} className="px-4 py-2 text-sm bg-primary text-primary-foreground font-semibold disabled:opacity-50 flex items-center gap-2">
          <FileText size={14} /> {saving ? "..." : "Verkaufen + PDF"}
        </button>
      </div>
    </Modal>
  );
}

function QRBillConfigDialog({ onClose }) {
  const [cfg, setCfg] = useState({ enabled: false, iban: "", creditor_name: "", creditor_address: "", creditor_zip: "", creditor_city: "", creditor_country: "CH" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/inventory/qrbill/config").then(r => setCfg(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/inventory/qrbill/config", cfg); toast.success("QR-Bill Einstellungen gespeichert"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  if (loading) return null;
  return (
    <Modal title="QR-Bill Einstellungen" onClose={onClose}>
      <p className="text-sm text-muted-foreground mb-3">
        Konfiguriere die Empfänger-Daten für Schweizer QR-Rechnungen. Wird auf jede neue Rechnung als Zahlteil aufgedruckt.
      </p>
      <div className="space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={cfg.enabled} onChange={e=>setCfg({...cfg, enabled: e.target.checked})} className="w-4 h-4" />
          <span className="text-sm font-semibold">QR-Bill auf Rechnungen aktivieren</span>
        </label>
        <Field label="IBAN *"><input type="text" value={cfg.iban} onChange={e=>setCfg({...cfg, iban: e.target.value})} placeholder="CH00 0000 0000 0000 0000 0" className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
        <Field label="Empfänger Firma/Name *"><input type="text" value={cfg.creditor_name} onChange={e=>setCfg({...cfg, creditor_name: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
        <Field label="Strasse + Nr"><input type="text" value={cfg.creditor_address} onChange={e=>setCfg({...cfg, creditor_address: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="PLZ"><input type="text" value={cfg.creditor_zip} onChange={e=>setCfg({...cfg, creditor_zip: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" /></Field>
          <Field label="Ort"><input type="text" value={cfg.creditor_city} onChange={e=>setCfg({...cfg, creditor_city: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm" /></Field>
          <Field label="Land"><input type="text" value={cfg.creditor_country} onChange={e=>setCfg({...cfg, creditor_country: e.target.value})} className="w-full px-3 py-2 border border-border bg-background text-sm rounded-sm font-mono" maxLength={2} /></Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-secondary">Abbrechen</button>
        <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-primary text-primary-foreground font-semibold disabled:opacity-50">{saving ? "..." : "Speichern"}</button>
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (<div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1 block">{label}</label>{children}</div>);
}

function Modal({ title, onClose, wide, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-card border border-border w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[90vh] overflow-y-auto rounded-sm`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-secondary"><X size={18} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
